/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import type { ParamDict } from "@utils/params";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { ChartPalette } from "../../utils/theme.ts";
import { LfoDisplay, LfoPoints } from "./lfoChart.ts";
import { CreateLfoCurveChart } from "./lfoRateDepthCurve.tsx";

export interface TremoloCurveProps {
  params: ParamDict;
  rateParam?: { min: number; max: number };
  intensityParam?: { min: number; max: number };
  onChange?: (key: string, value: unknown) => void;
}

export function TremoloCurve(props: TremoloCurveProps) {
  const mode: ChartMode = props.onChange ? "editor" : "inline";
  const editor = mode === "editor";
  const onChange = useLatest(props.onChange);
  const rateMin = props.rateParam?.min ?? LfoDisplay.rateMin;
  const rateMax = props.rateParam?.max ?? LfoDisplay.rateMax;
  const { ref, chart } = useChart(
    (container) =>
      CreateLfoCurveChart(container, mode, rateMin, rateMax, (_, p) => {
        onChange.current?.("rate", parseFloat(p.x.toFixed(2)));
        onChange.current?.("intensity", parseFloat(p.y.toFixed(3)));
      }),
    [mode, rateMin, rateMax],
  );

  const rate = props.params.rate.value as number;
  const intensity = props.params.intensity.value as number;
  const stereoPhase = props.params.stereoPhase.value as number;

  useEffect(() => {
    if (!chart) return;
    chart.curves.set([
      {
        id: "ghost",
        points: LfoPoints(rate, intensity, rateMin, rateMax, stereoPhase),
        color: ChartPalette.green,
        width: 1,
        opacity: editor ? 0.35 : 0.4,
        dashed: editor,
      },
      { id: "lfo", points: LfoPoints(rate, intensity, rateMin, rateMax) },
    ]);
    chart.handles?.set([
      {
        id: "rate",
        x: rate,
        y: intensity,
        label: `${rate.toFixed(1)}Hz`,
        sublabel: `${(intensity * 100).toFixed(0)}%`,
      },
    ]);
    chart.chart.render();
  }, [chart, rate, intensity, stereoPhase]);

  return <div ref={ref} />;
}
